import { PureComponent, type ReactNode } from 'react';

import { Timecode } from '@iconik-io/core/src/timecode';

import { filterNumber } from '../filters';
import {
  fieldRenderer,
  type TimecodeProps,
  type TimecodeState,
} from './renderers';

type Part = 'hours' | 'minutes' | 'seconds' | 'frames' | 'lastSeparator';

class TimecodeBase extends PureComponent<TimecodeProps, TimecodeState> {
  static getDerivedStateFromProps(
    nextProps: TimecodeProps,
    prevState: TimecodeState,
  ): Partial<TimecodeState> | null {
    if (nextProps.value === prevState.value) {
      return null;
    }
    return {
      value: nextProps.value,
      time: new Timecode(nextProps.timecodeOptions, {
        seconds: nextProps.value || 0,
      }),
    };
  }

  constructor(props: TimecodeProps) {
    super(props);
    this.state = {
      time: new Timecode(props.timecodeOptions, {
        seconds: props.value || 0,
      }),
      value: 0,
    };
  }

  onChangeHours = (value: string): void => {
    this.onChange({
      key: 'hours',
      value,
      min: 0,
      max: 59,
      length: 2,
    });
  };

  onChangeMinutes = (value: string): void => {
    this.onChange({
      key: 'minutes',
      value,
      min: 0,
      max: 59,
      length: 2,
    });
  };

  onChangeSeconds = (value: string): void => {
    this.onChange({
      key: 'seconds',
      value,
      min: 0,
      max: 59,
      length: 2,
    });
  };

  onChangeFrames = (value: string): void => {
    this.onChange({
      key: 'frames',
      value,
      min: 0,
      max: this.props.timecodeOptions.frameRate,
      length: Number(this.props.timecodeOptions.frameRate) >= 100 ? 3 : 2,
    });
  };

  onChange = ({
    key,
    value,
    min,
    max,
    length,
  }: {
    key: Part;
    value: string;
    min: number;
    max: number;
    length: number;
  }): void => {
    const filteredValue = filterNumber(value, min, max, length) || '00';
    const update: Pick<TimecodeState, Part> = { [key]: String(filteredValue) };
    this.setState(update);
    const nt = { ...this.state.time.toParts() };
    nt[key] = String(filteredValue);
    const smpte = `${nt.hours}:${nt.minutes}:${nt.seconds}${nt.lastSeparator}${nt.frames}`;
    const timecode = new Timecode(this.props.timecodeOptions, {
      smpte,
    });
    if (this.props.changeValue) {
      this.props.changeValue(timecode.toSeconds());
    }
  };

  onBlur = (): void => {
    this.setState({
      frames: undefined,
      seconds: undefined,
      minutes: undefined,
      hours: undefined,
    });
  };

  render(): ReactNode {
    const {
      onBlur,
      onChangeFrames,
      onChangeSeconds,
      onChangeMinutes,
      onChangeHours,
    } = this;
    const { renderer } = this.props;
    const componentRenderer = renderer || fieldRenderer;
    return componentRenderer(this.props, this.state, {
      onBlur,
      onChangeFrames,
      onChangeSeconds,
      onChangeMinutes,
      onChangeHours,
    });
  }
}

export default TimecodeBase;
